import * as React from 'react';
import { CheckCircle2, Inbox, type LucideIcon } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { StatePanel } from './state-panel';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.1.
 *
 * Two empties that are not the same: "you have not issued any DTEs yet"
 * (neutral, it calls for an action) and "nothing requires review" (positive, it
 * is the desired outcome). `tone` is the prop carrying the contract's weight —
 * painting both the same makes a clean inbox read as a failure.
 *
 * The distinction can NOT depend on colour alone (WCAG 1.4.1): by default each
 * tone uses an icon of a different shape (`Inbox` vs `CheckCircle2`), not merely
 * a different tint of the same glyph. If the screen passes its own `icon`, it
 * has already deliberately chosen a shape suited to its context — the
 * background/text tint `tone` contributes stays as reinforcement, not as the
 * only channel.
 */
const iconWrapperVariants = cva(
  'inline-flex size-icon-lg items-center justify-center rounded-full',
  {
    variants: {
      tone: {
        neutral: 'bg-subtle text-fg-muted',
        positive: 'bg-success-subtle text-success',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

const DEFAULT_TONE_ICON: Record<'neutral' | 'positive', LucideIcon> = {
  neutral: Inbox,
  positive: CheckCircle2,
};

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: React.ReactNode;
  /** The CTA: «Emitir DTE» | «Limpiar filtros». Composed by the screen. */
  action?: React.ReactNode;
  /** `positive` when the empty state is the good news ("nothing requires review"). */
  tone?: 'neutral' | 'positive';
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  className,
}: EmptyStateProps): React.JSX.Element {
  const Icon = icon ?? DEFAULT_TONE_ICON[tone];

  return (
    // `role="status"` (not `alert`): this is not an error, and it frequently
    // replaces a table's rows after a filter or a load. With no role that gets
    // announced, a screen reader that already read the table never learns it is
    // now empty — it keeps "seeing" the last known row until somebody navigates
    // there deliberately.
    //
    // The box is drawn by `StatePanel`, shared with `ErrorState`: both occupy
    // the same slot in `DataTable` and cannot look different. Why that shape was
    // chosen is in `state-panel.tsx`.
    <StatePanel role="status" className={className}>
      <span aria-hidden="true" className={cn(iconWrapperVariants({ tone }))}>
        <Icon className="size-icon-sm" />
      </span>
      <p className="text-body font-medium text-fg">{title}</p>
      {description ? (
        <div className="text-body-s text-fg-muted">{description}</div>
      ) : null}
      {action ? <div className="mt-stack-sm">{action}</div> : null}
    </StatePanel>
  );
}
